import { useApp } from 'contexts/app';
import { useSnackbar } from 'contexts/snackbar';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import assignmentService from 'services/assignment';
import {
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Icon,
    IconButton,
    Loading,
    Paper,
    ResponsiveDialog,
    Stack,
    Tag,
    Text,
} from 'shell/components';

// based on https://openai.com/pricing
// input: $10/1M tokens
// output: $30/1M tokens
const CENTS_PER_INPUT_TOKEN = 0.0005;
const CENTS_PER_OUTPUT_TOKEN = 0.0015;

const DebugDialog = ({ open, onClose }) => {
    const { currentAssignmentId, currentAssignment, submissions: allSubmissions } = useApp();
    const { message } = useSnackbar();

    const [batches, setBatches] = useState(null);

    const fetchBatches = useCallback(async () => {
        setBatches(null);
        const batches = await assignmentService.getAssignmentBatch(currentAssignmentId);
        setBatches(batches);
    }, [currentAssignmentId]);

    const copyToClipboard = async (content) => {
        await navigator.clipboard.writeText(content);
        message.success('Copied to clipboard!');
    };

    const renderBatches = () => {
        if (!batches) return <Loading />;
        return batches
            .sort((a, b) => b.created_on - a.created_on)
            .map((batch) => {
                try {
                    // submissions for this batch
                    const submissions = allSubmissions.filter(({ batch_id }) => batch_id === batch.id);

                    // how long did this batch take?
                    const duration = moment.duration(new moment(batch.finished_on).diff(new moment(batch.created_on)));

                    const numCompleted = submissions.filter(({ is_failed }) => !is_failed).length;
                    const numFailure = submissions.filter(({ is_failed }) => is_failed).length;
                    const successRate = ((numCompleted * 100) / (numCompleted + numFailure)).toFixed(0);

                    const numTotalGradingRetries = submissions.reduce((acc, submission) => {
                        return acc + submission.grading_retries;
                    }, 0);
                    const numTotalFeedbackRetries = submissions.reduce((acc, submission) => {
                        return acc + submission.feedback_retries;
                    }, 0);
                    const numTotalRetries = numTotalGradingRetries + numTotalFeedbackRetries;

                    const numAverageGradingRetries = (numTotalGradingRetries / submissions.length).toFixed(1);
                    const numAverageFeedbackRetries = (numTotalFeedbackRetries / submissions.length).toFixed(1);
                    const numAverageRetries = (numTotalRetries / submissions.length).toFixed(1);

                    const numMinGradingRetries = Math.min(...submissions.map(({ grading_retries }) => grading_retries));
                    const numMaxGradingRetries = Math.max(...submissions.map(({ grading_retries }) => grading_retries));
                    const numMinFeedbackRetries = Math.min(
                        ...submissions.map(({ feedback_retries }) => feedback_retries)
                    );
                    const numMaxFeedbackRetries = Math.max(
                        ...submissions.map(({ feedback_retries }) => feedback_retries)
                    );
                    const numMinRetries = Math.min(
                        ...submissions.map(
                            ({ grading_retries, feedback_retries }) => grading_retries + feedback_retries
                        )
                    );
                    const numMaxRetries = Math.max(
                        ...submissions.map(
                            ({ grading_retries, feedback_retries }) => grading_retries + feedback_retries
                        )
                    );

                    const totalInputTokens = submissions.reduce((acc, submission) => {
                        return (
                            acc +
                            submission.failed_usages.grading.reduce((acc, cur) => acc + (cur.prompt_tokens ?? 0), 0) +
                            submission.failed_usages.feedback.reduce((acc, cur) => {
                                return acc + (cur.prompt_tokens ?? 0);
                            }, 0) +
                            (submission.usage.prompt_tokens ?? 0)
                        );
                    }, 0);
                    const totalOutputTokens = submissions.reduce((acc, submission) => {
                        return (
                            acc +
                            submission.failed_usages.grading.reduce(
                                (acc, cur) => acc + (cur.completion_tokens ?? 0),
                                0
                            ) +
                            submission.failed_usages.feedback.reduce(
                                (acc, cur) => acc + (cur.completion_tokens ?? 0),
                                0
                            ) +
                            (submission.usage.completion_tokens ?? 0)
                        );
                    }, 0);
                    const totalCostInCents =
                        totalInputTokens * CENTS_PER_INPUT_TOKEN + totalOutputTokens * CENTS_PER_OUTPUT_TOKEN;
                    const averageCostInCents = totalCostInCents / submissions.length;

                    return (
                        <Paper sx={{ minHeight: '60px' }} key={batch.id} variant={'outlined'}>
                            <Stack sx={{ position: 'relative' }} spacing={3}>
                                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                    <Text variant={'p1'} weight={'semibold'}>
                                        {moment(batch.created_on).from(moment())}
                                    </Text>
                                    <Button
                                        color={'neutral'}
                                        variant={'borderless'}
                                        onClick={() => copyToClipboard(batch.id)}
                                    >
                                        ({batch.id})
                                    </Button>
                                    {!batch.is_finished && <Tag type={'invitation'} value={'pending'} />}
                                </Stack>
                                <Stack sx={{ position: 'absolute', top: '0', right: '0' }} spacing={1}>
                                    <Text align={'right'} color={'light'}>
                                        Created on {moment(batch.created_on).format('MMM D YYYY [at] hh:mma')}
                                    </Text>
                                    {batch.is_finished && (
                                        <Text align={'right'} color={'light'}>
                                            Finished on {moment(batch.finished_on).format('MMM D YYYY [at] hh:mma')}
                                        </Text>
                                    )}
                                    {batch.is_finished && (
                                        <Stack alignItems={'center'} direction={'row-reverse'} spacing={1}>
                                            <Text align={'right'} color={'light'}>
                                                {duration.get('minutes')} minutes {duration.get('seconds')} seconds
                                            </Text>
                                            <Icon
                                                sx={(theme) => ({ color: theme.palette.ensightful.neutral[700] })}
                                                kind={'clock'}
                                            />
                                        </Stack>
                                    )}
                                    <Text align={'right'} color={'light'}>
                                        Files: {batch.files.length}
                                    </Text>
                                </Stack>
                                {batch.is_finished && (
                                    <Stack spacing={2}>
                                        <Text
                                            sx={{ alignSelf: 'flex-start' }}
                                            tooltip={{
                                                component:
                                                    '"Completed" is how many submissions were eventually completed. "Failed" is how many submissions failed the validation the maximum number of times (20 times).',
                                                maxWidth: '300px',
                                            }}
                                            variant={'p1'}
                                        >
                                            <Text color={'success'} component={'span'} variant={'p1'}>
                                                {numCompleted} completed
                                            </Text>{' '}
                                            -{' '}
                                            <Text color={'error'} component={'span'} variant={'p1'}>
                                                {numFailure} {numFailure === 1 ? 'failure' : 'failures'}
                                            </Text>{' '}
                                            -{' '}
                                            <Text color={'light'} component={'span'} variant={'p1'}>
                                                {numTotalRetries} {numTotalRetries === 1 ? 'retry' : 'retries'}
                                            </Text>{' '}
                                            <Text component={'span'} variant={'p1'}>
                                                ({successRate}%)
                                            </Text>
                                        </Text>
                                        <Stack divider={<Divider />} spacing={2}>
                                            <Stack spacing={1}>
                                                <Text
                                                    sx={{ alignSelf: 'flex-start' }}
                                                    tooltip={{
                                                        component:
                                                            'Cost is estimated based on the number of input tokens and output tokens ($10/1M input tokens, $30/1M output tokens via https://openai.com/pricing).',
                                                        maxWidth: '200px',
                                                    }}
                                                    variant={'p1'}
                                                    weight={'semibold'}
                                                >
                                                    Estimated total cost: ${(totalCostInCents / 100).toFixed(2)}
                                                </Text>
                                                <Text variant={'p1'} weight={'semibold'}>
                                                    Average cost: ${(averageCostInCents / 100).toFixed(2)}
                                                </Text>
                                                <Text color={'light'} component={'i'}>
                                                    Total input tokens: {totalInputTokens}
                                                </Text>
                                                <Text color={'light'} component={'i'}>
                                                    Total output tokens: {totalOutputTokens}
                                                </Text>
                                                <Text>Total grading retries: {numTotalGradingRetries}</Text>
                                                <Text>Total feedback retries: {numTotalFeedbackRetries}</Text>
                                            </Stack>
                                            <Stack spacing={1}>
                                                <Text
                                                    sx={{ alignSelf: 'flex-start' }}
                                                    tooltip={{
                                                        component: 'How many retries did submissions take on average?',
                                                    }}
                                                    variant={'p1'}
                                                    weight={'semibold'}
                                                >
                                                    Average retries: {numAverageRetries}
                                                </Text>
                                                <Text
                                                    sx={{ alignSelf: 'flex-start' }}
                                                    tooltip={{
                                                        component:
                                                            'How many grading retries did submissions take on average?',
                                                    }}
                                                >
                                                    Average grading retries: {numAverageGradingRetries}
                                                </Text>
                                                <Text
                                                    sx={{ alignSelf: 'flex-start' }}
                                                    tooltip={{
                                                        component:
                                                            'How many feedback retries did submissions take on average?',
                                                    }}
                                                >
                                                    Average feedback retries: {numAverageFeedbackRetries}
                                                </Text>
                                            </Stack>
                                            <Stack spacing={1}>
                                                <Text
                                                    sx={{ alignSelf: 'flex-start' }}
                                                    tooltip={{
                                                        component:
                                                            'Shows the least/most number of retries that a submission went through. Grading and feedback retries are added together.',
                                                        maxWidth: '200px',
                                                    }}
                                                >
                                                    Least/most combined retries: {numMinRetries}/{numMaxRetries}
                                                </Text>
                                                <Text
                                                    sx={{ alignSelf: 'flex-start' }}
                                                    tooltip={{
                                                        component:
                                                            'Shows the least/most number of grading retries that a submission went through.',
                                                    }}
                                                >
                                                    Least/most grading retries: {numMinGradingRetries}/
                                                    {numMaxGradingRetries}
                                                </Text>
                                                <Text
                                                    sx={{ alignSelf: 'flex-start' }}
                                                    tooltip={{
                                                        component:
                                                            'Shows the least/most number of feedback retries that a submission went through.',
                                                    }}
                                                >
                                                    Least/most feedback retries: {numMinFeedbackRetries}/
                                                    {numMaxFeedbackRetries}
                                                </Text>
                                            </Stack>
                                        </Stack>
                                        <Divider />
                                        <Stack spacing={2}>
                                            <Button
                                                sx={{ alignSelf: 'flex-start' }}
                                                color={'neutral'}
                                                endIcon={
                                                    <Icon
                                                        kind={batch.expanded ? 'dropdownArrowUp' : 'dropdownArrowDown'}
                                                    />
                                                }
                                                size={'small'}
                                                variant={'borderless'}
                                                onClick={() =>
                                                    setBatches((current) => {
                                                        return current.map((b) => {
                                                            return b.id === batch.id
                                                                ? {
                                                                      ...b,
                                                                      expanded: !b.expanded,
                                                                  }
                                                                : b;
                                                        });
                                                    })
                                                }
                                            >
                                                {batch.expanded ? 'Hide' : 'Show'} Details
                                            </Button>
                                            {batch.expanded && (
                                                <Stack spacing={2}>
                                                    {submissions.map((submission) => {
                                                        const totalInputTokens =
                                                            submission.failed_usages.grading.reduce(
                                                                (acc, cur) => acc + (cur.prompt_tokens ?? 0),
                                                                0
                                                            ) +
                                                            submission.failed_usages.feedback.reduce(
                                                                (acc, cur) => acc + (cur.prompt_tokens ?? 0),
                                                                0
                                                            ) +
                                                            (submission.usage.prompt_tokens ?? 0);
                                                        const totalOutputTokens =
                                                            submission.failed_usages.grading.reduce(
                                                                (acc, cur) => acc + (cur.completion_tokens ?? 0),
                                                                0
                                                            ) +
                                                            submission.failed_usages.feedback.reduce(
                                                                (acc, cur) => acc + (cur.completion_tokens ?? 0),
                                                                0
                                                            ) +
                                                            (submission.usage.completion_tokens ?? 0);
                                                        const costInCents =
                                                            totalInputTokens * CENTS_PER_INPUT_TOKEN +
                                                            totalOutputTokens * CENTS_PER_OUTPUT_TOKEN;

                                                        return (
                                                            <Paper
                                                                sx={(theme) => ({
                                                                    borderColor:
                                                                        costInCents < averageCostInCents
                                                                            ? theme.palette.ensightful.success[500]
                                                                            : theme.palette.ensightful.error[500],
                                                                    borderWidth: '2px',
                                                                })}
                                                                key={submission.id}
                                                                variant={'outlined'}
                                                            >
                                                                <Stack divider={<Divider />} spacing={2}>
                                                                    <Stack
                                                                        sx={{ minWidth: 0 }}
                                                                        alignItems={'center'}
                                                                        direction={'row'}
                                                                        spacing={2}
                                                                    >
                                                                        <Text
                                                                            noWrap
                                                                            title={submission.data.name}
                                                                            weight={'semibold'}
                                                                        >
                                                                            {submission.data.name}
                                                                        </Text>
                                                                        <Button
                                                                            sx={{ flexShrink: 0 }}
                                                                            color={'neutral'}
                                                                            variant={'borderless'}
                                                                            onClick={() =>
                                                                                copyToClipboard(submission.id)
                                                                            }
                                                                        >
                                                                            ({submission.id})
                                                                        </Button>
                                                                    </Stack>
                                                                    <Stack spacing={1}>
                                                                        <Text>
                                                                            Grading retries:{' '}
                                                                            {submission.grading_retries}
                                                                        </Text>
                                                                        <Text>
                                                                            Feedback retries:{' '}
                                                                            {submission.feedback_retries}
                                                                        </Text>
                                                                        <Text>
                                                                            Total Input tokens consumed:{' '}
                                                                            {totalInputTokens}
                                                                        </Text>
                                                                        <Text>
                                                                            Total Output tokens consumed:{' '}
                                                                            {totalOutputTokens}
                                                                        </Text>
                                                                    </Stack>
                                                                    <Text weight={'semibold'}>
                                                                        Estimated cost: $
                                                                        {(costInCents / 100).toFixed(2)}
                                                                    </Text>
                                                                </Stack>
                                                            </Paper>
                                                        );
                                                    })}
                                                </Stack>
                                            )}
                                        </Stack>
                                    </Stack>
                                )}
                            </Stack>
                        </Paper>
                    );
                } catch (e) {
                    console.error(e);
                    return (
                        <Paper variant={'outlined'}>
                            <Text>Something went wrong.</Text>
                        </Paper>
                    );
                }
            });
    };

    useEffect(() => {
        if (!open) return;
        fetchBatches();
    }, [fetchBatches, open]);

    if (!open) return null;

    return (
        <ResponsiveDialog
            sx={{
                '& .MuiDialog-paper': {
                    width: '100%',
                    height: '100%',
                },
            }}
            open={open}
            scroll={'paper'}
            onClose={onClose}
        >
            <DialogTitle>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text variant={'h6'} weight={'medium'}>
                        Debug Info
                    </Text>
                    <IconButton kind={'exit'} size={'medium'} variant={'square'} onClick={onClose} />
                </Stack>
            </DialogTitle>
            <Divider />
            <DialogContent>
                <Stack spacing={4}>
                    <Stack spacing={2}>
                        <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'} spacing={4}>
                            <Stack sx={{ minWidth: 0 }} alignItems={'center'} direction={'row'} spacing={2}>
                                <Text noWrap variant={'sh2'} weight={'semibold'}>
                                    {currentAssignment.title}
                                </Text>
                                <Button
                                    sx={{ flexShrink: 0 }}
                                    color={'neutral'}
                                    size={'small'}
                                    variant={'borderless'}
                                    onClick={() => copyToClipboard(currentAssignmentId)}
                                >
                                    ({currentAssignmentId})
                                </Button>
                            </Stack>
                            <IconButton
                                disabled={!batches}
                                kind={'refresh'}
                                size={'small'}
                                tooltip={{ component: 'Refresh List' }}
                                variant={'square'}
                                onClick={fetchBatches}
                            >
                                Refresh
                            </IconButton>
                        </Stack>
                        <Stack spacing={2}>
                            <Text color={'light'}>Number of batches submitted: {batches?.length ?? '-'}</Text>
                            <Text color={'light'}>
                                Number of submissions processed: {allSubmissions?.length ?? '-'}
                            </Text>
                        </Stack>
                    </Stack>
                    <Stack spacing={4}>{renderBatches()}</Stack>
                </Stack>
            </DialogContent>
            <Divider />
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </ResponsiveDialog>
    );
};

export default DebugDialog;
